import React, { useState, useEffect } from 'react';
import { presupuestos, categorias as categoriasApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Calculator, Plus, Edit2, Trash2, TrendingUp, TrendingDown, 
  AlertTriangle, CheckCircle, Target, DollarSign 
} from 'lucide-react';

const MESES = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
];

const Presupuestos = () => {
  const [data, setData] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Filters
  const currentDate = new Date();
  const [filtroMes, setFiltroMes] = useState(currentDate.getMonth() + 1);
  const [filtroAnio, setFiltroAnio] = useState(currentDate.getFullYear());
  
  // Form
  const [formData, setFormData] = useState({
    categoria_id: '',
    monto_presupuestado: '',
    descripcion: '',
    mes: currentDate.getMonth() + 1,
    anio: currentDate.getFullYear()
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [presupuestosRes, resumenRes, categoriasRes] = await Promise.all([
        presupuestos.getAll({ mes: filtroMes, anio: filtroAnio }),
        presupuestos.getResumen({ mes: filtroMes, anio: filtroAnio }),
        categoriasApi.getAll()
      ]);
      setData(presupuestosRes.data);
      setResumen(resumenRes.data);
      setCategorias(categoriasRes.data.filter(c => c.tipo === 'egreso'));
    } catch (error) {
      toast.error('Error al cargar presupuestos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filtroMes, filtroAnio]);

  const handleSubmit = async () => {
    if (!formData.categoria_id || !formData.monto_presupuestado) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      if (editingItem) {
        await presupuestos.update(editingItem.id, formData);
        toast.success('Presupuesto actualizado');
      } else {
        await presupuestos.create(formData);
        toast.success('Presupuesto creado');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este presupuesto?')) return;
    try {
      await presupuestos.delete(id);
      toast.success('Presupuesto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      categoria_id: '',
      monto_presupuestado: '',
      descripcion: '',
      mes: filtroMes,
      anio: filtroAnio
    });
    setEditingItem(null);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      categoria_id: item.categoria_id,
      monto_presupuestado: item.monto_presupuestado,
      descripcion: item.descripcion || '',
      mes: item.mes,
      anio: item.anio
    });
    setShowDialog(true);
  };

  const getStatusBadge = (item) => {
    const porcentaje = item.monto_presupuestado > 0 
      ? (item.monto_real / item.monto_presupuestado) * 100 
      : 0;
    
    if (porcentaje > 100) {
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" />Excedido</Badge>;
    } else if (porcentaje > 80) {
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1"><TrendingUp className="w-3 h-3" />Alerta</Badge>;
    } else {
      return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"><CheckCircle className="w-3 h-3" />En Control</Badge>;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
  };

  const anios = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - 2 + i);

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="presupuestos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-6 h-6 text-primary" />
            Presupuestos
          </h1>
          <p className="text-muted-foreground text-sm">Control y seguimiento de presupuestos por categoría</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="new-budget-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Presupuesto
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-full sm:w-auto">
              <Label className="text-xs text-muted-foreground">Mes</Label>
              <Select value={String(filtroMes)} onValueChange={(v) => setFiltroMes(Number(v))}>
                <SelectTrigger className="w-full sm:w-[140px]" data-testid="filter-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESES.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-auto">
              <Label className="text-xs text-muted-foreground">Año</Label>
              <Select value={String(filtroAnio)} onValueChange={(v) => setFiltroAnio(Number(v))}>
                <SelectTrigger className="w-full sm:w-[100px]" data-testid="filter-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {anios.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {resumen && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Presupuestado</p>
                  <p className="text-2xl font-bold">{formatCurrency(resumen.total_presupuestado)}</p>
                </div>
                <Target className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Real</p>
                  <p className="text-2xl font-bold">{formatCurrency(resumen.total_real)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Diferencia</p>
                  <p className={`text-2xl font-bold ${resumen.desviacion <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(Math.abs(resumen.desviacion))}
                  </p>
                </div>
                {resumen.desviacion <= 0 
                  ? <TrendingDown className="w-8 h-8 text-emerald-500" />
                  : <TrendingUp className="w-8 h-8 text-red-500" />
                }
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">% Ejecución</p>
                  <p className="text-2xl font-bold">{resumen.porcentaje_uso?.toFixed(1) || 0}%</p>
                </div>
                <div className="w-12 h-12 relative">
                  <Progress value={Math.min(resumen.porcentaje_uso || 0, 100)} className="h-2 mt-4" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Budget List */}
      <Card>
        <CardHeader>
          <CardTitle>Presupuestos por Categoría</CardTitle>
          <CardDescription>
            {MESES.find(m => m.value === filtroMes)?.label} {filtroAnio}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando...</div>
          ) : data.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calculator className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay presupuestos para este período</p>
              <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowDialog(true); }}>
                Crear primer presupuesto
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {data.map((item) => {
                const porcentaje = item.monto_presupuestado > 0 
                  ? (item.monto_real / item.monto_presupuestado) * 100 
                  : 0;
                
                return (
                  <div key={item.id} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors" data-testid={`budget-item-${item.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{item.categoria_nombre}</h3>
                          {getStatusBadge(item)}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Presupuestado</p>
                            <p className="font-medium">{formatCurrency(item.monto_presupuestado)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Real</p>
                            <p className="font-medium">{formatCurrency(item.monto_real)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Diferencia</p>
                            <p className={`font-medium ${item.diferencia >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(item.diferencia)}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Desviación</p>
                            <p className={`font-medium ${item.porcentaje_desviacion <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {item.porcentaje_desviacion > 0 ? '+' : ''}{item.porcentaje_desviacion}%
                            </p>
                          </div>
                        </div>
                        <Progress 
                          value={Math.min(porcentaje, 100)} 
                          className={`h-2 mt-3 ${porcentaje > 100 ? '[&>div]:bg-red-500' : porcentaje > 80 ? '[&>div]:bg-amber-500' : ''}`}
                        />
                      </div>
                      <div className="flex gap-2 self-end sm:self-center">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`edit-budget-${item.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`delete-budget-${item.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Categoría de Gasto *</Label>
              <Select value={formData.categoria_id} onValueChange={(v) => setFormData({ ...formData, categoria_id: v })}>
                <SelectTrigger data-testid="form-category">
                  <SelectValue placeholder="Selecciona categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Mes</Label>
                <Select value={String(formData.mes)} onValueChange={(v) => setFormData({ ...formData, mes: Number(v) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Año</Label>
                <Select value={String(formData.anio)} onValueChange={(v) => setFormData({ ...formData, anio: Number(v) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {anios.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Monto Presupuestado *</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={formData.monto_presupuestado}
                onChange={(e) => setFormData({ ...formData, monto_presupuestado: parseFloat(e.target.value) || '' })}
                data-testid="form-amount"
              />
            </div>
            <div>
              <Label>Descripción (opcional)</Label>
              <Input
                placeholder="Notas adicionales..."
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} data-testid="submit-budget">
              {editingItem ? 'Guardar Cambios' : 'Crear Presupuesto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Presupuestos;
